import { useState, useEffect } from "react";
import { Envelope, Check, ShareNetwork, Copy, Link } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

function Toggle({ checked, onChange, label, description }) {
  return (
    <button onClick={() => onChange(!checked)} className="w-full flex items-center justify-between py-3 border-b border-[#1a1a1a] text-left">
      <div>
        <p className="text-sm text-white">{label}</p>
        {description && <p className="text-[10px] text-[#666] mt-0.5">{description}</p>}
      </div>
      <div className={`w-10 h-5 rounded-full transition-colors relative ${checked ? 'bg-[#ccff00]' : 'bg-[#333]'}`}>
        <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${checked ? 'left-5' : 'left-0.5'}`} />
      </div>
    </button>
  );
}

export function EmailPreferences({ user }) {
  const [prefs, setPrefs] = useState({ transactional: true, product_updates: true, marketing: true, unsubscribe_all: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.email) { setLoading(false); return; }
    axios.get(`${API}/email-preferences`)
      .then(res => setPrefs(res.data.preferences))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const save = async (newPrefs) => {
    setPrefs(newPrefs);
    try {
      await axios.put(`${API}/email-preferences`, newPrefs);
      toast.success("Email preferences updated");
    } catch (e) {
      toast.error("Failed to update preferences");
    }
  };

  if (!user?.email) {
    return (
      <div className="max-w-lg mx-auto px-3 sm:px-6 py-12 text-center">
        <Envelope size={48} weight="thin" className="mx-auto text-[#333] mb-4" />
        <p className="text-[#a3a3a3]">Sign in to manage email preferences</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-3 sm:px-6 py-6 sm:py-8">
      <h1 className="font-heading text-2xl text-white uppercase mb-2" data-testid="email-prefs-title">EMAIL PREFERENCES</h1>
      <p className="text-sm text-[#a3a3a3] mb-6">{user.email}</p>

      {loading ? (
        <div className="h-40 skeleton rounded-sm" />
      ) : (
        <div className="border border-[#262626] bg-[#171717] p-5" data-testid="email-prefs-form">
          <Toggle
            checked={prefs.transactional}
            onChange={(v) => save({ ...prefs, transactional: v })}
            label="Account & Billing"
            description="Subscription confirmations, cancellations, payment receipts"
          />
          <Toggle
            checked={prefs.product_updates}
            onChange={(v) => save({ ...prefs, product_updates: v })}
            label="Product Updates"
            description="New features, improvements, and platform changes"
          />
          <Toggle
            checked={prefs.marketing}
            onChange={(v) => save({ ...prefs, marketing: v })}
            label="Tips & Insights"
            description="Dynasty trade tips, strategy articles, seasonal updates"
          />
          <div className="mt-4 pt-3 border-t border-[#262626]">
            <Toggle
              checked={prefs.unsubscribe_all}
              onChange={(v) => save({ ...prefs, unsubscribe_all: v, transactional: !v, product_updates: !v, marketing: !v })}
              label="Unsubscribe from all"
              description="Stop all emails except critical account security notices"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function ReferralPage({ user }) {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.email) { setLoading(false); return; }
    axios.get(`${API}/referral/code`)
      .then(res => setReferralData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard!"));
  };

  if (!user?.email) {
    return (
      <div className="max-w-lg mx-auto px-3 sm:px-6 py-12 text-center">
        <ShareNetwork size={48} weight="thin" className="mx-auto text-[#333] mb-4" />
        <p className="text-[#a3a3a3]">Sign in to get your referral link</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-3 sm:px-6 py-6 sm:py-8">
      <h1 className="font-heading text-2xl text-white uppercase mb-2" data-testid="referral-title">
        REFER <span className="text-[#ccff00]">FRIENDS</span>
      </h1>
      <p className="text-sm text-[#a3a3a3] mb-6">Share Dynasty Trade Maker with your league-mates and earn rewards</p>

      {loading ? (
        <div className="h-40 skeleton rounded-sm" />
      ) : referralData ? (
        <div className="space-y-4">
          {/* Referral Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-[#262626] bg-[#171717] p-4 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] mb-1">YOUR CODE</p>
              <p className="font-mono font-bold text-xl text-[#ccff00]">{referralData.code}</p>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-4 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] mb-1">REFERRALS</p>
              <p className="font-mono font-bold text-xl text-white">{referralData.referral_count}</p>
            </div>
          </div>

          {/* Share Link */}
          <div className="border border-[#262626] bg-[#171717] p-5" data-testid="referral-link-section">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">YOUR REFERRAL LINK</p>
            <div className="flex gap-2">
              <Input value={referralData.url} readOnly className="h-10 bg-[#0a0a0a] border-[#262626] text-white font-mono text-xs" />
              <Button onClick={() => copyToClipboard(referralData.url)} className="h-10 px-4 btn-primary rounded-sm text-xs shrink-0" data-testid="copy-referral-btn">
                <Copy size={14} weight="bold" className="mr-1" /> COPY
              </Button>
            </div>
          </div>

          {/* Share Options */}
          <div className="border border-[#262626] bg-[#171717] p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">SHARE VIA</p>
            <div className="flex gap-2">
              <Button
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out Dynasty Trade Maker - AI-powered dynasty fantasy football trade tool! ${referralData.url}`)}`, '_blank')}
                className="flex-1 h-9 bg-transparent border border-[#262626] text-[#a3a3a3] hover:border-[#ccff00] hover:text-[#ccff00] rounded-sm text-[10px] uppercase"
              >
                TWITTER/X
              </Button>
              <Button
                onClick={() => copyToClipboard(`Check out Dynasty Trade Maker - AI dynasty trade tool for Sleeper leagues! ${referralData.url}`)}
                className="flex-1 h-9 bg-transparent border border-[#262626] text-[#a3a3a3] hover:border-[#ccff00] hover:text-[#ccff00] rounded-sm text-[10px] uppercase"
              >
                COPY MESSAGE
              </Button>
            </div>
          </div>

          {/* How it works */}
          <div className="bg-[#0a0a0a] border border-[#262626] p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">HOW IT WORKS</p>
            <ol className="text-xs text-[#a3a3a3] space-y-1.5 list-decimal pl-4">
              <li>Share your referral link with friends who play dynasty fantasy football</li>
              <li>They sign up using your link and get full free-tier access</li>
              <li>You earn credit toward your next month when they upgrade to Pro</li>
            </ol>
          </div>
        </div>
      ) : (
        <p className="text-[#666] text-sm">Failed to load referral data</p>
      )}
    </div>
  );
}
